import { memo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Zap, BookOpen, Layers } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Course } from '@/types/courses';

interface CourseCardProps {
  course: Course;
  progress: { total: number; done: number; percent: number };
  counts: { modules: number; lessons: number };
  isSubscribed: boolean;
  onSelect: (courseId: string) => void;
}

export const CourseCard = memo(function CourseCard({
  course,
  progress,
  counts,
  isSubscribed,
  onSelect,
}: CourseCardProps) {
  const isComingSoon = course.status?.toLowerCase() === 'coming soon';
  const description = course.overview || course.tile_description || '';

  const ctaLabel = isComingSoon
    ? 'Coming Soon'
    : !isSubscribed
      ? 'Upgrade to Access'
      : progress.percent === 0
        ? 'Start Course'
        : 'Continue Learning';

  return (
    <Card
      className={cn(
        'flex flex-col hover:border-orange-300 dark:hover:border-orange-700 hover:shadow-lg transition-all cursor-pointer group',
        isComingSoon && 'opacity-70 cursor-default'
      )}
      onClick={() => !isComingSoon && onSelect(course.id)}
    >
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start mb-1">
          {course.is_featured && !isComingSoon && (
            <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-2.5 py-0.5 rounded-full text-xs font-medium">
              Featured
            </span>
          )}
          {isComingSoon && (
            <span className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-2.5 py-0.5 rounded-full text-xs font-medium">
              Coming Soon
            </span>
          )}
          {progress.percent > 0 && !isComingSoon && (
            <div className="flex items-center text-xs font-semibold text-green-600 dark:text-green-400 ml-auto">
              <Zap className="w-3 h-3 mr-1 fill-current" />
              in progress
            </div>
          )}
        </div>
        <CardTitle className="text-xl text-gray-900 dark:text-white group-hover:text-[#F37022] transition-colors text-left">
          {course.title}
        </CardTitle>
        {description && (
          <CardDescription className="line-clamp-2 mt-1 text-left">
            {description}
          </CardDescription>
        )}
      </CardHeader>

      <CardContent className="flex-1 pb-2">
        {!isComingSoon && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center">
                <Layers className="h-4 w-4 mr-1" />
                <span>
                  {counts.modules} {counts.modules === 1 ? 'module' : 'modules'}
                </span>
              </div>
              <div className="flex items-center">
                <BookOpen className="h-4 w-4 mr-1" />
                <span>
                  {counts.lessons} {counts.lessons === 1 ? 'lesson' : 'lessons'}
                </span>
              </div>
            </div>
            <div className="space-y-1">
              <div className="flex justify-between text-xs text-gray-500">
                <span>{progress.done}/{progress.total} lessons</span>
                <span>{progress.percent}%</span>
              </div>
              <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#F37022] transition-all"
                  style={{ width: `${progress.percent}%` }}
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="pt-4 border-t border-gray-100 dark:border-gray-800">
        {isComingSoon ? (
          <p className="w-full text-center text-sm text-gray-500 dark:text-gray-400 italic">
            This course will be available soon
          </p>
        ) : (
          <Button
            className={cn(
              'w-full transition-colors',
              isSubscribed
                ? 'bg-gray-50 text-gray-900 hover:bg-[#F37022] hover:text-white dark:bg-gray-800 dark:text-white dark:hover:bg-[#F37022] group-hover:bg-[#F37022] group-hover:text-white'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200'
            )}
          >
            {ctaLabel}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
});
